// https://icseng.atlassian.net/wiki/x/gYr2Bw
import axios, { type AxiosResponse } from '@fs/zion-axios'
import { ROOTSTECH_SERVICE as SERVICE } from '../../serviceNames'
import { chunkArray } from '../../../lib/helpers/functionalHelpers'

type WatchHistoryOptions = { sessionIds?: string[]; count?: number; offset?: string | null }
type WatchHistoryEntry = { id: string; seekTime?: number; mostRecentPosition?: number; completed: boolean }

const BASE = `${SERVICE}/watch-history/`
const ENTRIES = `${BASE}entries`
const ENABLED = `${BASE}enabled`
// The backend is also limited to 50
const CHUNK_LIMIT = 25

export const getWatchHistory = async (): Promise<AxiosResponse> => axios.get(BASE)
export const getHistoryEntryForSession = async (sessionId: string): Promise<AxiosResponse> =>
  axios.get(`${ENTRIES}/${sessionId}`)
export const getWatchHistoryEnabled = async (): Promise<AxiosResponse> => axios.get(ENABLED)

const makeHistoryEntriesRequest = async ({
  sessionIds = [],
  count = CHUNK_LIMIT,
  offset = null,
}: WatchHistoryOptions): Promise<AxiosResponse> => {
  const params = new URLSearchParams()
  params.append('count', String(count))
  if (offset) params.append('offset', offset)
  sessionIds.forEach((id) => params.append('sid', id))
  return axios.get(ENTRIES, { params })
}
export const getSomeHistoryEntries = (options: WatchHistoryOptions): Promise<AxiosResponse> =>
  makeHistoryEntriesRequest(options)

export const getHistoryEntries = async (sessionIds: string[] = []): Promise<unknown[]> => {
  const chunkedRequests = chunkArray(sessionIds, CHUNK_LIMIT)
  const requests = chunkedRequests.map((ids) => makeHistoryEntriesRequest({ sessionIds: ids }))
  const responses = await Promise.all(requests)
  const results = responses.map(({ data }) => data.results)
  return results.flat()
}

export const createWatchHistoryEntry = async ({
  id,
  seekTime,
  mostRecentPosition,
  completed,
}: WatchHistoryEntry): Promise<AxiosResponse> =>
  axios.post(ENTRIES, { sessionId: id, mostRecentPosition: mostRecentPosition ?? seekTime, completed })

export const setWatchHistoryEnabled = async (shouldEnable: boolean): Promise<AxiosResponse> =>
  axios.put(ENABLED, { enabled: shouldEnable })

export const deleteHistoryEntryForSession = async (sessionId: string): Promise<AxiosResponse> =>
  axios.delete(`${ENTRIES}/${sessionId}`)
export const purgeWatchHistory = async (): Promise<AxiosResponse> => axios.delete(BASE)
